import { DataTypesEnum } from '../data/enums';
import { InspectionModel } from '../data/types';
import { InspectionModelHelpers } from './inspection-model-helpers';

/** Where an Inspection on this device stands in reaching the server (#11590). */
export enum DeliveryState {
  /** Created on the device and never submitted. */
  InProgress = 'In progress',
  /** Opened again for changes; nothing is sent until it is re-submitted. */
  Reopened = 'Reopened',
  /** Submitted, but the server has not accepted the record yet. */
  Queued = 'Queued',
  /** The record has been accepted, but some photos have not been received. */
  PhotosPending = 'Photos pending',
  /** Failed to send 3 times for a reason retrying won't fix. */
  NeedsAttention = 'Needs attention',
  /** The record and every photo are on the server, and nothing is waiting to send. */
  Delivered = 'Delivered',
}

export const ELIGIBLE_FOR_BACKGROUND_SEND = new Set([
  DeliveryState.Queued,
  DeliveryState.PhotosPending,
]);

/** Counted failures after which background sync stops and the inspector is told. */
const NEEDS_ATTENTION_AFTER = 3;

const hasPhotoNotReceived = (inspection: InspectionModel): boolean =>
  inspection.inspectionCategoryTypes.some((categoryType) =>
    categoryType.observations.some(
      (observation) =>
        observation.observableItem?.dataTypeId === DataTypesEnum.image &&
        !observation.fileReceived,
    ),
  );

/**
 * The server's "completed" flag is not read: the server also clears it while it reviews
 * an equipment-history conflict. The "submitted" marker separates a submitted Inspection
 * from a draft; when it is missing on a record with unsent parts, the flags don't say
 * for certain, so the Inspection is In progress and nothing is sent behind the
 * inspector's back. Photos only count as pending when this device submitted them: a
 * server copy can carry photos another device has not delivered yet.
 */
export function deliveryStateOf(inspection: InspectionModel): DeliveryState {
  const state = stateBeforeFailuresCounted(inspection);
  return ELIGIBLE_FOR_BACKGROUND_SEND.has(state) &&
    (inspection.sendFailures?.counted ?? 0) >= NEEDS_ATTENTION_AFTER
    ? DeliveryState.NeedsAttention
    : state;
}

function stateBeforeFailuresCounted(
  inspection: InspectionModel,
): DeliveryState {
  if (inspection.reopened === true) return DeliveryState.Reopened;

  const submitted = inspection.submitRequested === true;
  const recordAccepted =
    inspection.id > 0 && InspectionModelHelpers.isSynced(inspection);
  if (!recordAccepted) {
    return submitted ? DeliveryState.Queued : DeliveryState.InProgress;
  }
  if (submitted && hasPhotoNotReceived(inspection)) {
    return DeliveryState.PhotosPending;
  }
  return DeliveryState.Delivered;
}
